(function () {
  if (customElements.get('cart-drawer-related-products')) return;

  class CartDrawerRelatedProducts extends HTMLElement {
    constructor() {
      super();
      this.track = this.querySelector('.cart-drawer-related__track');
      this.viewport = this.querySelector('.cart-drawer-related__viewport');
      this.nav = this.querySelector('.cart-drawer-related__nav');
      this.dotsWrap = this.querySelector('.cart-drawer-related__dots');
      this.prevBtn = this.querySelector('.cart-drawer-related__nav-btn--prev');
      this.nextBtn = this.querySelector('.cart-drawer-related__nav-btn--next');
    }

    connectedCallback() {
      this.fetchRecommendations();
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.slide(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.slide(1));
      if (this.viewport) this.viewport.addEventListener('scroll', () => this.updateNavState(), { passive: true });
    }

    async fetchRecommendations() {
      const url = this.getAttribute('data-url');
      if (!url) return;

      const currentId = Number(this.getAttribute('data-current-id'));
      const collectionHandle = this.getAttribute('data-collection');
      const excludeIds = new Set([currentId]);
      let relatedProducts = [];

      try {
        if (collectionHandle) {
          try {
            const collectionRes = await fetch(`/collections/${collectionHandle}/products.json?limit=10`);
            if (collectionRes.ok) {
              const collectionData = await collectionRes.json();
              const collectionProducts = (collectionData && collectionData.products) || [];
              relatedProducts = collectionProducts
                .filter((p) => !excludeIds.has(p.id))
                .slice(0, 3)
                .map((p) => this.normalizeProduct(p));
              relatedProducts.forEach((p) => excludeIds.add(p.id));
            }
          } catch (e) {
            console.warn('Collection products fetch failed', e);
          }
        }

        if (relatedProducts.length < 3) {
          try {
            const relatedRes = await fetch(url);
            if (relatedRes.ok) {
              const relatedData = await relatedRes.json();
              const fallbackRelated = (relatedData && relatedData.products) || [];
              const needed = 3 - relatedProducts.length;
              const extra = fallbackRelated.filter((p) => !excludeIds.has(p.id)).slice(0, needed);
              extra.forEach((p) => excludeIds.add(p.id));
              relatedProducts = [...relatedProducts, ...extra];
            }
          } catch (e) {
            console.warn('Shopify recommendations fetch failed', e);
          }
        }

        let otherProducts = [];
        try {
          const otherRes = await fetch('/products.json?limit=12');
          if (otherRes.ok) {
            const otherData = await otherRes.json();
            const allProducts = (otherData && otherData.products) || [];
            otherProducts = allProducts
              .filter((p) => !excludeIds.has(p.id))
              .sort(() => Math.random() - 0.5)
              .slice(0, 3)
              .map((p) => this.normalizeProduct(p));
          }
        } catch (e) {
          console.warn('Other products fetch failed', e);
        }

        const products = [...relatedProducts, ...otherProducts];
        if (!products.length) { this.remove(); return; }
        this.renderProducts(products);
      } catch (err) {
        console.warn('Cart drawer related products error:', err);
        this.remove();
      }
    }

    normalizeProduct(p) {
      const variant = p.variants && p.variants[0] ? p.variants[0] : {};
      const priceCents = variant.price ? Math.round(parseFloat(variant.price) * 100) : 0;
      const compareCents = variant.compare_at_price ? Math.round(parseFloat(variant.compare_at_price) * 100) : null;
      const imageSrc = (p.images && p.images[0] && p.images[0].src) || (p.image && p.image.src) || '';
      return {
        id: p.id,
        title: p.title,
        url: `/products/${p.handle}`,
        featured_image: imageSrc,
        price: priceCents,
        compare_at_price: compareCents,
        variants: [{ id: variant.id }],
      };
    }

    renderProducts(products) {
      this.track.innerHTML = products.map((p) => this.productTemplate(p)).join('');
      this.track.querySelectorAll('.cart-drawer-related__add').forEach((btn) => {
        btn.addEventListener('click', (e) => this.handleAddToCart(e));
      });
      this.buildDots(products.length);
      this.nav.hidden = products.length <= 1;
      this.updateNavState();
    }

    productTemplate(product) {
      const image = product.featured_image || (product.images && product.images[0]) || '';
      const price = this.formatMoney(product.price);
      const compareAtPrice = product.compare_at_price && product.compare_at_price > product.price
        ? this.formatMoney(product.compare_at_price) : null;
      const variantId = product.variants && product.variants[0] ? product.variants[0].id : product.id;
      return `
        <li class="cart-drawer-related__item">
          <a href="${product.url}" class="cart-drawer-related__card">
            <div class="cart-drawer-related__image-wrap">
              ${image ? `<img src="${image}" alt="${this.escapeHtml(product.title)}" loading="lazy" width="300" height="300">` : ''}
            </div>
            <p class="cart-drawer-related__title">${this.escapeHtml(product.title)}</p>
            <p class="cart-drawer-related__price">${compareAtPrice ? `<s>${compareAtPrice}</s>` : ''}${price}</p>
          </a>
          <button type="button" class="cart-drawer-related__add" data-variant-id="${variantId}">Add to cart</button>
        </li>
      `;
    }

    async handleAddToCart(event) {
      const button = event.currentTarget;
      const variantId = button.getAttribute('data-variant-id');
      button.disabled = true;
      const originalText = button.textContent;
      button.textContent = 'Adding...';

      try {
        const drawerEl = document.querySelector('cart-drawer') || document.getElementById('CartDrawer') || document.getElementById('cart-drawer');
        const sectionId =
          (drawerEl && (drawerEl.dataset.id || drawerEl.dataset.section || drawerEl.getAttribute('data-section-id'))) ||
          'cart-drawer';

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections: [sectionId, 'cart-icon-bubble', 'cart-notification-product', 'cart-drawer'],
            sections_url: window.location.pathname,
          }),
        });

        if (!response.ok) throw new Error('Add to cart failed');
        const data = await response.json();
        button.textContent = 'Added';

        this.refreshDrawerUI(data, drawerEl, sectionId);

        setTimeout(() => { button.disabled = false; button.textContent = originalText; }, 1500);
      } catch (err) {
        console.warn('Cart drawer related add-to-cart error:', err);
        button.textContent = 'Error';
        setTimeout(() => { button.disabled = false; button.textContent = originalText; }, 1500);
      }
    }

    refreshDrawerUI(data, drawerEl, sectionId) {
      if (drawerEl && typeof drawerEl.renderContents === 'function') {
        try {
          drawerEl.renderContents(data);
        } catch (e) {
          console.warn('renderContents failed, falling back to manual refresh', e);
        }
      }

      if (data.sections) {
        const htmlForId = data.sections[sectionId] || data.sections['cart-drawer'];
        if (htmlForId && drawerEl) {
          try {
            const parser = new DOMParser();
            const newDoc = parser.parseFromString(htmlForId, 'text/html');
            const newInner =
              newDoc.querySelector('cart-drawer') ||
              newDoc.getElementById('CartDrawer') ||
              newDoc.getElementById('cart-drawer') ||
              newDoc.body.firstElementChild;
            if (newInner) {
              drawerEl.innerHTML = newInner.innerHTML;
            }
          } catch (e) {
            console.warn('Manual drawer HTML swap failed', e);
          }
        }
      }

      if (data.sections && data.sections['cart-icon-bubble']) {
        const bubbleTarget = document.getElementById('cart-icon-bubble');
        if (bubbleTarget) {
          const parser = new DOMParser();
          const newDoc = parser.parseFromString(data.sections['cart-icon-bubble'], 'text/html');
          const newBubble = newDoc.getElementById('cart-icon-bubble');
          if (newBubble) bubbleTarget.innerHTML = newBubble.innerHTML;
        }
      }

      if (drawerEl) {
        if (typeof drawerEl.open === 'function') {
          drawerEl.open();
        } else {
          drawerEl.classList.add('active', 'is-open', 'drawer--is-open', 'animate');
          drawerEl.setAttribute('open', '');
          document.body.classList.add('overflow-hidden', 'drawer-open', 'cart-drawer-open');
        }
      }

      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { cart: data } }));
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: data } }));
    }

    buildDots(count) {
      if (!this.dotsWrap) return;
      this.dotsWrap.innerHTML = Array.from({ length: count })
        .map((_, i) => `<button type="button" class="cart-drawer-related__dot" data-index="${i}" aria-label="Go to slide ${i + 1}"></button>`)
        .join('');
      this.dotsWrap.querySelectorAll('.cart-drawer-related__dot').forEach((dot) => {
        dot.addEventListener('click', () => {
          const index = Number(dot.getAttribute('data-index'));
          const item = this.track.children[index];
          if (item) item.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
        });
      });
    }

    updateNavState() {
      if (!this.viewport) return;
      const items = Array.from(this.track.children);
      if (!items.length) return;
      const viewportLeft = this.viewport.scrollLeft;
      let activeIndex = 0, closestDistance = Infinity;
      items.forEach((item, i) => {
        const distance = Math.abs(item.offsetLeft - viewportLeft);
        if (distance < closestDistance) { closestDistance = distance; activeIndex = i; }
      });
      if (this.dotsWrap) {
        this.dotsWrap.querySelectorAll('.cart-drawer-related__dot').forEach((dot, i) => {
          dot.setAttribute('aria-current', i === activeIndex ? 'true' : 'false');
        });
      }
      if (this.prevBtn) this.prevBtn.disabled = activeIndex === 0;
      if (this.nextBtn) this.nextBtn.disabled = activeIndex === items.length - 1;
    }

    slide(direction) {
      const items = Array.from(this.track.children);
      if (!items.length) return;
      const viewportLeft = this.viewport.scrollLeft;
      let currentIndex = 0, closestDistance = Infinity;
      items.forEach((item, i) => {
        const distance = Math.abs(item.offsetLeft - viewportLeft);
        if (distance < closestDistance) { closestDistance = distance; currentIndex = i; }
      });
      const targetIndex = Math.min(Math.max(currentIndex + direction, 0), items.length - 1);
      items[targetIndex].scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
    }

    formatMoney(cents) {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.theme?.moneyFormat) {
        return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
      }
      return `$${(cents / 100).toFixed(2)}`;
    }

    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str || '';
      return div.innerHTML;
    }
  }

  customElements.define('cart-drawer-related-products', CartDrawerRelatedProducts);
})();